import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Play, Pause, Clock, Calendar, ChevronRight, ChevronDown, Trash2, Edit2, X, Save, Cloud, WifiOff, AlertCircle, LogOut, Lock, UserPlus, Settings, LayoutList, LayoutGrid, PlusCircle } from 'lucide-react';
import { initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { getFirestore, collection, doc, setDoc, onSnapshot, getDoc, updateDoc } from 'firebase/firestore';

// --- Firebase Initialization ---
// PASTE YOUR CONFIG HERE
const firebaseConfig = {
  apiKey: "AIzaSyB0kn8Y1DUsfQLpl-OfGtP1lEIAls6HxHQ",
  authDomain: "timesheet-michael.firebaseapp.com",
  projectId: "timesheet-michael",
  storageBucket: "timesheet-michael.firebasestorage.app",
  messagingSenderId: "675244867380",
  appId: "1:675244867380:web:78031030112228a0072f61",
  measurementId: "G-V4GTDHR400"
};

let app, auth, db;
try {
  // Check if config is empty or placeholder
  if (!firebaseConfig.apiKey || firebaseConfig.apiKey === "...") {
    throw new Error("Config missing");
  }
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  db = getFirestore(app);
} catch (e) {
  console.warn("Firebase not initialized yet. Please paste your config.");
}

const appId = 'time-tracker-v1'; 

const App = () => {
  // --- State ---
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  
  // Settings State
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    dayStartHour: 0, // 0 = Midnight, 3 = 3 AM (shifts day window)
    weekStartDay: 0  // 0 = Sunday, 1 = Monday
  });

  const [todayKey, setTodayKey] = useState(''); // Initialized in effect
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Login Form State
  const [isSignUp, setIsSignUp] = useState(false); 
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');

  // Active Timer State
  const [isRunning, setIsRunning] = useState(false);
  const [startTime, setStartTime] = useState(null);
  
  // Local Derived State
  const [currentSessionDuration, setCurrentSessionDuration] = useState(0);

  // Data Store
  const [history, setHistory] = useState({});
  
  // UI State
  const [historyViewMode, setHistoryViewMode] = useState('daily'); // 'daily' | 'weekly'
  const [expandedItems, setExpandedItems] = useState({}); 
  
  // Modals
  const [editingSession, setEditingSession] = useState(null);
  const [addingEntry, setAddingEntry] = useState(null); 
  const [confirmModal, setConfirmModal] = useState(null);

  // --- Helpers ---
  
  const getAdjustedDate = useCallback((date) => {
    if (!date) return new Date();
    const adjusted = new Date(date);
    adjusted.setHours(adjusted.getHours() - preferences.dayStartHour);
    return adjusted;
  }, [preferences.dayStartHour]);

  const getDateKey = useCallback((date) => {
    const adjusted = getAdjustedDate(date);
    const year = adjusted.getFullYear();
    const month = String(adjusted.getMonth() + 1).padStart(2, '0');
    const day = String(adjusted.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, [getAdjustedDate]);

  function formatTime(ms) {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const pad = (num) => num.toString().padStart(2, '0');
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  }

  function getTimeInputValue(timestamp) {
    const date = new Date(timestamp);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  function updateTimestampFromInput(originalTimestamp, timeString) {
    const [hours, minutes] = timeString.split(':').map(Number);
    const date = new Date(originalTimestamp);
    date.setHours(hours);
    date.setMinutes(minutes);
    date.setSeconds(0); 
    return date.getTime();
  }

  // Initialize todayKey once preferences load
  useEffect(() => {
    setTodayKey(getDateKey(new Date()));
  }, [getDateKey]);


  // --- Logic: Midnight Crossing ---
  const handleDayCrossing = useCallback(async (oldKey, newKey) => {
    if (!user || !db) return;

    if (isRunning && startTime) {
      const now = Date.now();
      const duration = now - startTime; 
      
      const session = {
        id: Date.now(),
        start: startTime,
        end: now,
        duration: duration,
        note: 'Auto-split at day boundary'
      };

      const oldDayRef = doc(db, 'artifacts', appId, 'users', user.uid, 'daily_logs', oldKey);
      const oldDaySnap = await getDoc(oldDayRef);
      let currentData = oldDaySnap.exists() ? oldDaySnap.data() : { total: 0, sessions: [] };
      
      const newData = {
        total: currentData.total + duration,
        sessions: [session, ...currentData.sessions]
      };
      await setDoc(oldDayRef, newData);

      const activeRef = doc(db, 'artifacts', appId, 'users', user.uid, 'app_state', 'active_timer');
      await setDoc(activeRef, { isRunning: true, startTime: now });
    }
    
    setTodayKey(newKey);
  }, [user, isRunning, startTime]);


  // --- Effects ---

  // 1. Authentication Listener
  useEffect(() => {
    if (!auth) {
      setAuthLoading(false);
      return;
    }
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // 2. Firestore Listeners
  useEffect(() => {
    if (!user || !db) return;

    const settingsRef = doc(db, 'artifacts', appId, 'users', user.uid, 'settings', 'preferences');
    const unsubSettings = onSnapshot(settingsRef, (docSnap) => {
      if (docSnap.exists()) {
        setPreferences(docSnap.data());
      }
    });

    const activeSessionRef = doc(db, 'artifacts', appId, 'users', user.uid, 'app_state', 'active_timer');
    const unsubSession = onSnapshot(activeSessionRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setIsRunning(data.isRunning);
        setStartTime(data.startTime);
      } else {
        setIsRunning(false);
        setStartTime(null);
      }
    });

    const historyCollectionRef = collection(db, 'artifacts', appId, 'users', user.uid, 'daily_logs');
    const unsubHistory = onSnapshot(historyCollectionRef, (snapshot) => {
      const newHistory = {};
      snapshot.docs.forEach(doc => {
        newHistory[doc.id] = doc.data();
      });
      setHistory(newHistory);
    });

    return () => {
      unsubSession();
      unsubHistory();
      unsubSettings();
    };
  }, [user]);

  // 3. Clock
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      setCurrentTime(now);
      
      const newDateKey = getDateKey(now);
      if (newDateKey !== todayKey && todayKey !== '') {
        handleDayCrossing(todayKey, newDateKey);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [todayKey, handleDayCrossing, getDateKey]); 

  // 4. Timer Visuals
  useEffect(() => {
    let interval;
    if (isRunning && startTime) {
      interval = setInterval(() => {
        setCurrentSessionDuration(Date.now() - startTime);
      }, 100);
    } else {
      setCurrentSessionDuration(0);
    }
    return () => clearInterval(interval);
  }, [isRunning, startTime]);


  // --- Data Processing: Weekly Aggregation ---
  const processedHistory = useMemo(() => {
    if (historyViewMode === 'daily') {
      return Object.keys(history).sort().reverse().map(key => ({
        key,
        title: key === todayKey ? `${key} (Today)` : key,
        data: history[key],
        isToday: key === todayKey
      }))
      // CHANGED: Removed "|| item.isToday" so today also disappears if total is 0
      .filter(item => item.data.total > 0);
    } else {
      // Weekly Aggregation
      const weeks = {};
      Object.keys(history).forEach(dateKey => {
        if (history[dateKey].total === 0) return;

        const [y, m, d] = dateKey.split('-').map(Number);
        const dateObj = new Date(y, m - 1, d);
        
        const dayOfWeek = dateObj.getDay(); // 0=Sun, 6=Sat
        const diff = (dayOfWeek < preferences.weekStartDay ? 7 : 0) + dayOfWeek - preferences.weekStartDay;
        dateObj.setDate(dateObj.getDate() - diff);
        
        const weekKey = `Week of ${dateObj.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
        
        if (!weeks[weekKey]) {
          weeks[weekKey] = { total: 0, sessions: [], daysCount: 0 };
        }
        weeks[weekKey].total += history[dateKey].total;
        
        const sessionsWithDate = history[dateKey].sessions.map(s => ({...s, originalDateKey: dateKey}));
        weeks[weekKey].sessions = [...weeks[weekKey].sessions, ...sessionsWithDate];
        weeks[weekKey].daysCount += 1;
      });

      if (isRunning) {
         const now = getAdjustedDate(new Date());
         const dayOfWeek = now.getDay();
         const diff = (dayOfWeek < preferences.weekStartDay ? 7 : 0) + dayOfWeek - preferences.weekStartDay;
         now.setDate(now.getDate() - diff);
         const currentWeekKey = `Week of ${now.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
         
         if (weeks[currentWeekKey]) {
           weeks[currentWeekKey].total += currentSessionDuration;
         } else if (currentSessionDuration > 0) {
           weeks[currentWeekKey] = { total: currentSessionDuration, sessions: [], daysCount: 1 };
         }
      }

      return Object.keys(weeks).sort((a,b) => {
         const getDate = (str) => new Date(str.replace('Week of ', '') + ' ' + new Date().getFullYear());
         return getDate(b) - getDate(a);
      }).map(key => ({
        key,
        title: key,
        data: weeks[key],
        isToday: false
      }))
      .filter(item => item.data.total > 0);
    }
  }, [history, historyViewMode, todayKey, preferences.weekStartDay, isRunning, currentSessionDuration, getAdjustedDate]);


  // --- Actions ---

  const handleSavePreferences = async () => {
    if (!user) return;
    const settingsRef = doc(db, 'artifacts', appId, 'users', user.uid, 'settings', 'preferences');
    await setDoc(settingsRef, preferences);
    setShowSettings(false);
    setTodayKey(getDateKey(new Date()));
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthError('');
    if (!auth) return;
    try {
      if (isSignUp) await createUserWithEmailAndPassword(auth, email, password);
      else await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      setAuthError(err.message);
    }
  };

  const handleLogout = () => {
    if (auth) signOut(auth);
    setHistory({});
    setIsRunning(false);
    setEmail('');
    setPassword('');
  };

  const handleStart = async () => {
    if (!user || !db) return;
    const activeRef = doc(db, 'artifacts', appId, 'users', user.uid, 'app_state', 'active_timer');
    await setDoc(activeRef, { isRunning: true, startTime: Date.now() });
  };

  const handleStop = async () => {
    if (!user || !isRunning || !db) return;
    
    const now = Date.now();
    const duration = now - startTime;
    
    const newSession = {
      id: Date.now(),
      start: startTime,
      end: now,
      duration: duration
    };

    const activeRef = doc(db, 'artifacts', appId, 'users', user.uid, 'app_state', 'active_timer');
    await setDoc(activeRef, { isRunning: false, startTime: null });

    const dayRef = doc(db, 'artifacts', appId, 'users', user.uid, 'daily_logs', todayKey);
    const daySnap = await getDoc(dayRef);
    let dayData = daySnap.exists() ? daySnap.data() : { total: 0, sessions: [] };
    
    await setDoc(dayRef, {
      total: dayData.total + duration,
      sessions: [newSession, ...dayData.sessions]
    });
    
    setCurrentSessionDuration(0);
  };

  const toggleItemView = (key) => {
    setExpandedItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const triggerClearHistory = () => {
    if (!user) return;
    setConfirmModal({
      message: "Are you sure you want to clear all history? This cannot be undone.",
      onConfirm: async () => {
        if (!db) return;
        const promises = Object.keys(history).map(key => {
           const ref = doc(db, 'artifacts', appId, 'users', user.uid, 'daily_logs', key);
           return setDoc(ref, { total: 0, sessions: [] });
        });
        await Promise.all(promises);
        setConfirmModal(null);
      }
    });
  };

  const triggerDeleteSession = (dateKey, sessionId) => {
    if (!user) return;
    setConfirmModal({
      message: "Delete this session entry?",
      onConfirm: async () => {
        if (!db) return;
        const dayRef = doc(db, 'artifacts', appId, 'users', user.uid, 'daily_logs', dateKey);
        const daySnap = await getDoc(dayRef);
        
        if (daySnap.exists()) {
          const dayData = daySnap.data();
          const updatedSessions = dayData.sessions.filter(s => s.id !== sessionId);
          const newTotal = updatedSessions.reduce((acc, s) => acc + s.duration, 0);
          
          await updateDoc(dayRef, {
            sessions: updatedSessions,
            total: newTotal
          });
        }
        setConfirmModal(null);
      }
    });
  };

  const openEditModal = (dateKey, index, session) => {
    setEditingSession({
      dateKey,
      sessionId: session.id,
      originalDate: session.start,
      startTimeStr: getTimeInputValue(session.start),
      endTimeStr: getTimeInputValue(session.end),
      error: null
    });
  };

  const openAddModal = () => {
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-CA'); 
    const timeStr = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
    
    setAddingEntry({
      dateStr,
      startTimeStr: timeStr,
      endTimeStr: timeStr,
      error: null
    });
  };

  const addToDayLog = async (key, session) => {
     const ref = doc(db, 'artifacts', appId, 'users', user.uid, 'daily_logs', key);
     const snap = await getDoc(ref);
     let data = { total: 0, sessions: [] };
     if (snap.exists()) data = snap.data();
     const newSessions = [...data.sessions, session].sort((a, b) => b.start - a.start);
     await setDoc(ref, { total: data.total + session.duration, sessions: newSessions });
  };

  const saveEdit = async () => {
    if (!editingSession || !user || !db) return;
    const { dateKey, sessionId, originalDate, startTimeStr, endTimeStr } = editingSession;
    const newStart = updateTimestampFromInput(originalDate, startTimeStr);
    let newEnd = updateTimestampFromInput(originalDate, endTimeStr);
    if (newEnd < newStart) {
        const endDate = new Date(newEnd);
        endDate.setDate(endDate.getDate() + 1); 
        newEnd = endDate.getTime();
    }
    const newDuration = newEnd - newStart;

    const startKey = getDateKey(new Date(newStart));
    const endKey = getDateKey(new Date(newEnd));

    const oldDayRef = doc(db, 'artifacts', appId, 'users', user.uid, 'daily_logs', dateKey);
    const oldDaySnap = await getDoc(oldDayRef);
    if (oldDaySnap.exists()) {
      const data = oldDaySnap.data();
      const sessionToRemove = data.sessions.find(s => s.id === sessionId);
      const removeDuration = sessionToRemove ? sessionToRemove.duration : 0;
      const updatedSessions = data.sessions.filter(s => s.id !== sessionId);
      await setDoc(oldDayRef, { ...data, sessions: updatedSessions, total: data.total - removeDuration });
    }

    if (startKey === endKey) {
       await addToDayLog(startKey, { id: sessionId, start: newStart, end: newEnd, duration: newDuration });
    } else {
       const [y, m, d] = endKey.split('-').map(Number);
       const splitDate = new Date(y, m - 1, d, preferences.dayStartHour, 0, 0, 0);
       const splitTime = splitDate.getTime();
       await addToDayLog(startKey, { id: sessionId, start: newStart, end: splitTime, duration: splitTime - newStart, note: 'Split (Part 1)' });
       await addToDayLog(endKey, { id: sessionId + '_2', start: splitTime, end: newEnd, duration: newEnd - splitTime, note: 'Split (Part 2)' });
    }
    setEditingSession(null);
  };

  const saveNewEntry = async () => {
    if (!addingEntry || !user || !db) return;
    const { dateStr, startTimeStr, endTimeStr } = addingEntry;

    const [y, m, d] = dateStr.split('-').map(Number);
    const [sh, sm] = startTimeStr.split(':').map(Number);
    const startObj = new Date(y, m - 1, d, sh, sm, 0);
    const newStart = startObj.getTime();

    const [eh, em] = endTimeStr.split(':').map(Number);
    let endObj = new Date(y, m - 1, d, eh, em, 0);
    
    if (endObj < startObj) endObj.setDate(endObj.getDate() + 1);
    const newEnd = endObj.getTime();
    const newDuration = newEnd - newStart;

    if (newDuration <= 0) {
        setAddingEntry(prev => ({ ...prev, error: "Duration must be greater than 0" }));
        return;
    }

    const startKey = getDateKey(new Date(newStart));
    const endKey = getDateKey(new Date(newEnd));
    const sessionId = Date.now();

    if (startKey === endKey) {
        await addToDayLog(startKey, { id: sessionId, start: newStart, end: newEnd, duration: newDuration, note: 'Manual Entry' });
    } else {
        const [ky, km, kd] = endKey.split('-').map(Number);
        const splitDate = new Date(ky, km - 1, kd, preferences.dayStartHour, 0, 0, 0);
        const splitTime = splitDate.getTime();
        await addToDayLog(startKey, { id: sessionId, start: newStart, end: splitTime, duration: splitTime - newStart, note: 'Manual Entry (Part 1)' });
        await addToDayLog(endKey, { id: sessionId + '_2', start: splitTime, end: newEnd, duration: newEnd - splitTime, note: 'Manual Entry (Part 2)' });
    }
    setAddingEntry(null);
  };

  // --- RENDER ---

  if (!auth) return <div className="p-8 text-center">Config Missing</div>;
  if (authLoading) return <div className="min-h-screen flex justify-center">Loading...</div>;

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-100 flex justify-center p-4">
        <div className="w-full max-w-xs bg-white p-8 rounded-2xl shadow-xl">
          <div className="flex justify-center mb-6">
            <div className="bg-blue-100 p-3 rounded-full">
              {isSignUp ? <UserPlus className="text-blue-600" size={32} /> : <Lock className="text-blue-600" size={32} />}
            </div>
          </div>
          <h2 className="text-xl font-bold text-center text-slate-800 mb-6">
            {isSignUp ? "Create Account" : "Private Access"}
          </h2>
          <form onSubmit={handleAuth} className="space-y-4">
            <input type="email" placeholder="Email" required className="w-full border p-2 rounded" value={email} onChange={e => setEmail(e.target.value)} />
            <input type="password" placeholder="Password" required className="w-full border p-2 rounded" value={password} onChange={e => setPassword(e.target.value)} />
            {authError && <p className="text-red-500 text-xs">{authError}</p>}
            <button type="submit" className="w-full bg-slate-900 text-white py-3 rounded transition-colors">
              {isSignUp ? "Sign Up" : "Sign In"}
            </button>
            <div className="text-center">
              <button type="button" onClick={() => setIsSignUp(!isSignUp)} className="text-xs text-blue-600 hover:underline">
                {isSignUp ? "Have an account? Sign In" : "Create account"}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  const todayData = history[todayKey] || { total: 0, sessions: [] };
  const todayTotal = todayData.total + (isRunning ? currentSessionDuration : 0);

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900 font-sans flex justify-center p-4">
      <div className="w-full max-w-md flex flex-col gap-4 relative">
        {showSettings && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm rounded-2xl p-4">
             <div className="bg-white p-6 rounded-xl shadow-2xl w-full max-w-sm border border-slate-200">
               <h3 className="font-bold text-lg mb-4 flex items-center gap-2"><Settings size={18}/> Preferences</h3>
               <div className="space-y-4">
                 <div>
                   <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Day Starts At</label>
                   <select className="w-full border p-2 rounded" value={preferences.dayStartHour} onChange={(e) => setPreferences({...preferences, dayStartHour: Number(e.target.value)})}>
                     {Array.from({length: 24}, (_, i) => (<option key={i} value={i}>{i === 0 ? 'Midnight (Local)' : `${i}:00 ${i < 12 ? 'AM' : 'PM'}`}</option>))}
                   </select>
                 </div>
                 <div>
                   <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Start of Week</label>
                   <select className="w-full border p-2 rounded" value={preferences.weekStartDay} onChange={(e) => setPreferences({...preferences, weekStartDay: Number(e.target.value)})}>
                     <option value={0}>Sunday</option>
                     <option value={1}>Monday</option>
                     <option value={6}>Saturday</option>
                   </select>
                 </div>
               </div>
               <div className="mt-6 flex justify-end gap-2">
                 <button onClick={() => setShowSettings(false)} className="px-4 py-2 text-slate-500 hover:bg-slate-100 rounded">Cancel</button>
                 <button onClick={handleSavePreferences} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Save</button>
               </div>
             </div>
          </div>
        )}

        {editingSession && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/20 backdrop-blur-sm rounded-2xl">
            <div className="bg-white p-6 rounded-xl shadow-2xl w-11/12 max-w-sm border border-slate-200">
              <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2"><Edit2 size={18} /> Edit Session</h3>
              <div className="space-y-4">
                {editingSession.error && (<div className="text-xs text-red-500 bg-red-50 p-2 rounded">{editingSession.error}</div>)}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Start Time</label>
                  <input type="time" value={editingSession.startTimeStr} onChange={(e) => setEditingSession({...editingSession, startTimeStr: e.target.value, error: null})} className="w-full border border-slate-300 rounded-lg p-2 font-mono text-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">End Time</label>
                  <input type="time" value={editingSession.endTimeStr} onChange={(e) => setEditingSession({...editingSession, endTimeStr: e.target.value, error: null})} className="w-full border border-slate-300 rounded-lg p-2 font-mono text-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-6">
                <button onClick={() => setEditingSession(null)} className="flex items-center justify-center gap-2 p-2 rounded-lg text-slate-600 hover:bg-slate-100 font-medium"><X size={18} /> Cancel</button>
                <button onClick={saveEdit} className="flex items-center justify-center gap-2 p-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 font-medium shadow-lg shadow-blue-200"><Save size={18} /> Save</button>
              </div>
            </div>
          </div>
        )}

        {addingEntry && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/20 backdrop-blur-sm rounded-2xl">
            <div className="bg-white p-6 rounded-xl shadow-2xl w-11/12 max-w-sm border border-slate-200">
              <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2"><PlusCircle size={18} /> Add Entry</h3>
              <div className="space-y-4">
                {addingEntry.error && (<div className="text-xs text-red-500 bg-red-50 p-2 rounded">{addingEntry.error}</div>)}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Date</label>
                  <input type="date" value={addingEntry.dateStr} onChange={(e) => setAddingEntry({...addingEntry, dateStr: e.target.value, error: null})} className="w-full border border-slate-300 rounded-lg p-2 font-mono text-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Start</label>
                    <input type="time" value={addingEntry.startTimeStr} onChange={(e) => setAddingEntry({...addingEntry, startTimeStr: e.target.value, error: null})} className="w-full border border-slate-300 rounded-lg p-2 font-mono text-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">End</label>
                    <input type="time" value={addingEntry.endTimeStr} onChange={(e) => setAddingEntry({...addingEntry, endTimeStr: e.target.value, error: null})} className="w-full border border-slate-300 rounded-lg p-2 font-mono text-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-6">
                <button onClick={() => setAddingEntry(null)} className="flex items-center justify-center gap-2 p-2 rounded-lg text-slate-600 hover:bg-slate-100 font-medium"><X size={18} /> Cancel</button>
                <button onClick={saveNewEntry} className="flex items-center justify-center gap-2 p-2 rounded-lg bg-green-600 text-white hover:bg-green-700 font-medium shadow-lg shadow-green-200"><Save size={18} /> Add</button>
              </div>
            </div>
          </div>
        )}

        {confirmModal && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm rounded-2xl p-4">
             <div className="bg-white p-6 rounded-xl shadow-2xl w-full max-w-sm text-center">
               <p className="font-medium mb-6">{confirmModal.message}</p>
               <div className="grid grid-cols-2 gap-3">
                 <button onClick={() => setConfirmModal(null)} className="py-2 bg-slate-100 rounded">Cancel</button>
                 <button onClick={confirmModal.onConfirm} className="py-2 bg-red-600 text-white rounded">Confirm</button>
               </div>
             </div>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="bg-slate-900 p-4 text-white flex justify-between items-start">
            <div>
              <div className="flex items-center gap-2 text-slate-400 text-xs mb-1"><Cloud size={12} /> {user.email}</div>
              <h1 className="text-lg font-bold flex items-center gap-2">Time Tracker</h1>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowSettings(true)} className="p-2 hover:bg-slate-800 rounded text-slate-400 hover:text-white transition-colors"><Settings size={18} /></button>
              <button onClick={handleLogout} className="p-2 hover:bg-slate-800 rounded text-slate-400 hover:text-white transition-colors"><LogOut size={18} /></button>
            </div>
          </div>

          <div className="p-8 text-center relative">
            {isRunning ? (
              <div className="animate-pulse">
                <div className="text-green-600 text-xs font-bold uppercase tracking-widest mb-2">Active Session</div>
                <div className="text-7xl font-mono font-bold text-slate-800 tracking-tighter mb-4">{formatTime(currentSessionDuration)}</div>
                <div className="inline-flex items-center gap-2 bg-slate-100 px-3 py-1 rounded-full text-slate-500 text-sm">
                  <span>Total Today:</span><span className="font-mono font-bold text-slate-700">{formatTime(todayTotal)}</span>
                </div>
              </div>
            ) : (
               <div>
                 <div className="text-slate-400 text-xs font-bold uppercase tracking-widest mb-2">Total Time Today</div>
                 <div className="text-6xl font-mono font-bold text-slate-800 tracking-tighter mb-4">{formatTime(todayTotal)}</div>
                 <div className="text-slate-400 text-sm">Ready to start?</div>
               </div>
            )}
          </div>

          <div className="px-8 pb-8">
            {!isRunning ? (
              <button onClick={handleStart} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-xl py-4 flex items-center justify-center gap-2 font-semibold text-lg shadow-lg shadow-blue-200 transition-all active:scale-95"><Play size={24} fill="currentColor" /> Start Day</button>
            ) : (
              <button onClick={handleStop} className="w-full bg-red-500 hover:bg-red-600 text-white rounded-xl py-4 flex items-center justify-center gap-2 font-semibold text-lg shadow-lg shadow-red-200 transition-all active:scale-95"><Pause size={24} fill="currentColor" /> Stop Timer</button>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-md overflow-hidden flex flex-col max-h-96">
          <div className="p-3 border-b border-slate-100 bg-slate-50 flex justify-between items-center sticky top-0 z-10">
            <div className="flex items-center gap-1 bg-slate-200 p-1 rounded-lg">
              <button onClick={() => setHistoryViewMode('daily')} className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${historyViewMode === 'daily' ? 'bg-white shadow text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}>Daily</button>
              <button onClick={() => setHistoryViewMode('weekly')} className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${historyViewMode === 'weekly' ? 'bg-white shadow text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}>Weekly</button>
            </div>
            <div className="flex items-center gap-2">
                <button onClick={openAddModal} className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors" title="Add Entry"><PlusCircle size={18} /></button>
                <button onClick={triggerClearHistory} className="text-xs text-slate-400 hover:text-red-500 font-medium">Clear</button>
            </div>
          </div>

          <div className="overflow-y-auto flex-1 p-2 space-y-2">
            {processedHistory.length === 0 ? (
              <div className="text-center py-8 text-slate-400 text-sm italic">No history yet.</div>
            ) : (
              processedHistory.map(item => {
                const isExpanded = expandedItems[item.key];
                return (
                  <div key={item.key} className="border border-slate-200 rounded-lg overflow-hidden bg-white">
                     <button onClick={() => toggleItemView(item.key)} className={`w-full flex items-center justify-between p-3 transition-colors ${item.isToday ? 'bg-blue-50/50' : 'hover:bg-slate-50'}`}>
                        <div className="flex items-center gap-2">
                          {isExpanded ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
                          <div className="flex flex-col items-start">
                            <span className={`text-sm font-medium ${item.isToday ? 'text-blue-700' : 'text-slate-700'}`}>{item.title}</span>
                            {historyViewMode === 'weekly' && (<span className="text-[10px] text-slate-400">{item.data.daysCount} days active</span>)}
                          </div>
                        </div>
                        <span className="font-mono font-bold text-slate-800">{formatTime(item.data.total)}</span>
                      </button>

                      {isExpanded && (
                        <div className="bg-slate-50 border-t border-slate-100 p-2 space-y-1">
                           {item.data.sessions.length === 0 ? (
                             <p className="text-xs text-slate-400 px-2">No details.</p>
                           ) : (
                             item.data.sessions.map((sess, idx) => (
                               <div key={sess.id || idx} className="group flex justify-between items-center text-xs text-slate-600 px-3 py-2 bg-white border border-slate-100 rounded shadow-sm hover:shadow-md transition-shadow">
                                 <div className="flex flex-col">
                                   <span>{new Date(sess.start).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})} - {new Date(sess.end).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</span>
                                   {sess.note && <span className="text-[10px] text-slate-400 italic">{sess.note}</span>}
                                 </div>
                                 <div className="flex items-center gap-3">
                                    <span className="font-mono font-bold">{formatTime(sess.duration)}</span>
                                    {historyViewMode === 'daily' && (
                                      <div className="flex items-center gap-1 opacity-100 md:opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button onClick={(e) => { e.stopPropagation(); openEditModal(item.key, idx, sess); }} className="p-1.5 text-blue-400 hover:text-blue-600 hover:bg-blue-50 rounded" title="Edit"><Edit2 size={14} /></button>
                                        <button onClick={(e) => { e.stopPropagation(); triggerDeleteSession(item.key, sess.id); }} className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded" title="Delete"><Trash2 size={14} /></button>
                                      </div>
                                    )}
                                 </div>
                               </div>
                             ))
                           )}
                        </div>
                      )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default App;